import React, { useState } from 'react';
import { Row, Col, Typography, Tag } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import {
  crucesToSlotGrid,
  slotGridToCruces,
  intercambiarSlots,
} from './llave-octavos.util';

const { Text } = Typography;

const SlotRow = ({
  entry,
  matchIndex,
  slot,
  draggable,
  isDragging,
  onDragStart,
  onDragEnd,
  onDrop,
  onDragOver,
}) => {
  const vacio = !entry?.inscripcion_id;

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver?.();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(matchIndex, slot);
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 40,
        padding: '6px 10px',
        marginBottom: 4,
        background: isDragging ? '#404040' : vacio ? '#252525' : '#2d2d2d',
        border: vacio ? '1px dashed #555' : '1px solid #434343',
        borderRadius: 6,
        fontSize: 13,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {draggable && !vacio && (
        <span
          draggable
          onDragStart={(e) => {
            e.stopPropagation();
            onDragStart(matchIndex, slot, entry);
          }}
          onDragEnd={onDragEnd}
          style={{ cursor: 'grab', color: '#888', display: 'flex' }}
        >
          <HolderOutlined />
        </span>
      )}
      {vacio ? (
        <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
          Bye / vacío
        </Text>
      ) : (
        <>
          <span style={{ flex: 1, lineHeight: 1.3 }}>{entry.etiqueta}</span>
          {entry.clasificacion && (
            <Tag style={{ margin: 0, fontSize: 11 }}>{entry.clasificacion}</Tag>
          )}
        </>
      )}
    </div>
  );
};

const MatchCard = ({
  matchIndex,
  partido,
  dragSource,
  onDragStart,
  onDragEnd,
  onDrop,
}) => (
  <div
    style={{
      padding: 10,
      background: '#1f1f1f',
      border: '1px solid #333',
      borderRadius: 10,
      marginBottom: 12,
    }}
  >
    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
      Partido {matchIndex + 1}
    </Text>
    <SlotRow
      entry={partido.a}
      matchIndex={matchIndex}
      slot="a"
      draggable
      isDragging={dragSource?.matchIndex === matchIndex && dragSource?.slot === 'a'}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
    />
    <SlotRow
      entry={partido.b}
      matchIndex={matchIndex}
      slot="b"
      draggable
      isDragging={dragSource?.matchIndex === matchIndex && dragSource?.slot === 'b'}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
    />
  </div>
);

/**
 * Editor drag & drop de la primera ronda del cuadro eliminatorio.
 */
const LlaveOctavosEditor = ({ cruces, onChange, rondaLabel = 'Primera ronda' }) => {
  const [dragSource, setDragSource] = useState(null);
  const grid = crucesToSlotGrid(cruces);

  const handleDragStart = (matchIndex, slot, entry) => {
    if (!entry?.inscripcion_id) return;
    setDragSource({ matchIndex, slot });
  };

  const handleDrop = (destMatchIndex, destSlot) => {
    if (!dragSource) return;
    if (
      dragSource.matchIndex === destMatchIndex
      && dragSource.slot === destSlot
    ) {
      setDragSource(null);
      return;
    }
    const srcEntry = grid[dragSource.matchIndex]?.[dragSource.slot];
    const destEntry = grid[destMatchIndex]?.[destSlot];
    if (
      destEntry?.inscripcion_id
      && srcEntry?.inscripcion_id
      && destEntry.inscripcion_id === srcEntry.inscripcion_id
    ) {
      setDragSource(null);
      return;
    }
    const nextGrid = intercambiarSlots(grid, dragSource, {
      matchIndex: destMatchIndex,
      slot: destSlot,
    });
    onChange(slotGridToCruces(nextGrid));
    setDragSource(null);
  };

  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
        Arrastrá las parejas entre casilleros de {rondaLabel.toLowerCase()} para ajustar el cuadro.
        Los huecos vacíos son bye (avance directo).
      </Text>
      <Row gutter={[12, 0]}>
        {grid.map((partido, matchIndex) => (
          <Col xs={24} sm={12} lg={8} xl={6} key={`match-${matchIndex}`}>
            <MatchCard
              matchIndex={matchIndex}
              partido={partido}
              dragSource={dragSource}
              onDragStart={handleDragStart}
              onDragEnd={() => setDragSource(null)}
              onDrop={handleDrop}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default LlaveOctavosEditor;
